/**
 * String utility functions
 */

/**
 * Strip any prefix up to and including the last / or \. e.g. "a/b/file.png" → "file.png"
 */
export function pathBasename(p: string): string {
  return p.replace(/^.*[/\\]/, '');
}

/**
 * Strip the last path segment (and its / or \). e.g. "a/b/file.png" → "a/b"
 */
export function pathDir(p: string): string {
  return p.replace(/[/\\][^/\\]*$/, '');
}

/**
 * Strip the first path segment (and its / or \). e.g. "folder/file.png" → "file.png", "root/sub/a.png" → "sub/a.png".
 * Useful for webkitRelativePath where the first segment is the chosen directory name.
 */
export function pathWithoutFirstSegment(p: string): string {
  return p.replace(/^[^/\\]+[/\\]?/, '');
}

/**
 * Use forward slashes only. e.g. "a\\b\\file.png" → "a/b/file.png"
 */
export function normalizePath(p: string): string {
  return p.replace(/\\/g, '/');
}

/** True if the path/filename has a common image extension (png, jpg, jpeg, gif, webp, bmp), case-insensitive. */
export function isImagePath(p: string): boolean {
  return /\.(png|jpg|jpeg|gif|webp|bmp)$/i.test(p);
}

/**
 * Finds a value in a path-keyed record by matching the given path. Tries exact key (normalized and raw),
 * then key ending with '/' + path, then basename match. Use e.g. for path → blob URL lookups.
 */
export function findValueByPathKey(path: string, pathKeyedRecord: Record<string, string>): string | undefined {
  const norm = normalizePath(path);
  if (pathKeyedRecord[norm]) { return pathKeyedRecord[norm]; }

  if (pathKeyedRecord[path]) { return pathKeyedRecord[path]; }
  const name = pathBasename(norm);
  const match = Object.keys(pathKeyedRecord).find(
    (fp) =>
      normalizePath(fp) === norm
      || fp.endsWith(`/${norm}`)
      || pathBasename(normalizePath(fp)) === name
  );
  return match ? pathKeyedRecord[match] : undefined;
}

/**
 * Resolves a relative path against a base file path. If base has a directory and relative has no '/',
 * returns dir + '/' + relative; otherwise returns relative unchanged.
 */
export function resolveRelativePath(baseFilePath: string, relativePath: string): string {
  const dir = pathDir(baseFilePath);
  if (!dir || relativePath.includes('/')) { return relativePath; }
  return dir + '/' + relativePath;
}

/**
 * Capitalizes the first character of a string and lowercases the rest
 * @param str - The string to capitalize
 * @returns The capitalized string
 */
export function capitalize(str: string): string {
  if (!str) { return str; }
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
* Removes the file extension from a given file name.
*
* @param fileName - The file name with extension.
* @returns The file name without extension.
*/
export function RemoveExtension(fileName: string): string {
  return fileName.split('.').slice(0, -1).join('.');
}

/**
* Replaces all occurrences of keys in a string with their corresponding values from a map object.
*
* @param str - The input string.
* @param mapObj - The map object containing key-value pairs to be replaced in the string.
* @returns The modified string with all occurrences replaced.
*/
export function ReplaceAll(str: string, mapObj: Record<string, string>): string {
  const regex = new RegExp(`\\b(?:${Object.keys(mapObj).join('|')})\\b`, 'gi');

  return str.replace(regex, (matched) => mapObj[matched]);
}

export function FormatSecondsToMinutes(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;

  return `${PadWithZero(mins)}:${PadWithZero(secs)}`;
}

export function PadWithZero(number: number): string {
  return number.toString().padStart(2, '0');
}
